import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getAppViewerModuleTempDashboardUrl } from '@components/common/Dashboard/utils/getTempDashboardUrl';
import { dashboardsSelector as tempDashboardsSelector } from '@store/tempDashboard/selectors';
import { message, Tooltip } from 'antd';
import { chartIntervalSelector } from '@src/store/trends/birdChart/selectors';
import moment from 'moment-timezone';
import { filterSelector } from '@src/store/common/selectors';
import localStorage from '@src/utils/localStorage';
import { useAtomValue } from 'jotai';
import { uuidAtom } from './useGlobalFilterUUID';
import { genGlobalFiltersLocalstorageKey } from '@src/pages/AppTimeline/useGlobalFilters';
import { StyledButton } from '@src/components/dimension-panel-hz/dimension-table/styled';
import { CopyOutlined } from '@ant-design/icons';
import copy from 'copy-to-clipboard';
import { CellWrapper } from '../styled';

interface IAppViewerCellProps {
  type: 'user' | 'client';
  userId?: string;
  clientId?: string;
  flowId?: number;
}

function AppViewerCell(props: IAppViewerCellProps): JSX.Element {
  const { userId, clientId, flowId, type = 'user' } = props;
  const globalFilters = useSelector(filterSelector);
  const id = type === 'user' ? userId : clientId;
  const tempDashboards = useSelector(tempDashboardsSelector) || {};
  const keys = Object.keys(tempDashboards)
    .filter((key) => /^[0-9]*$/.test(key))
    .map(Number);
  const maxTempId = keys.length === 0 ? 1 : Math.max.apply(null, keys) + 1;
  const chartInterval: {
    from: string;
    to: string;
  } = useSelector(chartIntervalSelector);
  const { from, to } = chartInterval;
  const interval = `${moment(from).format()}/${moment(to).format()}`;

  const uuid = useAtomValue(uuidAtom);
  const handleClick = () => {
    if (globalFilters && globalFilters.length > 0) {
      localStorage.set({
        key: genGlobalFiltersLocalstorageKey(uuid),
        value: globalFilters,
        expires: 7 * 1000 * 60 * 60 * 24, // 7 days
      });
    }
  };

  const tempDashboardUrl = useMemo(() => {
    const params = {
      viewerId: userId,
      clientId,
      interval,
      source: 'users-table',
      key: uuid,
      flowId,
    };

    return getAppViewerModuleTempDashboardUrl(params, String(maxTempId));
  }, [clientId, interval, maxTempId, userId, uuid, flowId]);

  return (
    <CellWrapper>
      <Tooltip title={id} placement="topLeft">
        {id === 'UNKNOWN' ? (
          <span>{id}</span>
        ) : (
          <a
            style={{
              color: '#1890ff',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
            href={tempDashboardUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
            onContextMenu={handleClick}
          >
            <span>{id}</span>
          </a>
        )}
      </Tooltip>
      <div className="action-bar">
        <StyledButton
          type="text"
          icon={<CopyOutlined />}
          $isAppModule
          onClick={() => {
            if (id !== undefined && copy(id, { format: 'text/plain' })) {
              message.info('Copied to clipboard');
            }
          }}
        />
      </div>
    </CellWrapper>
  );
}

export default React.memo(AppViewerCell);
